/**
 * FE-owned null-secret discovery + injection for recipe import yaml (§4 of the
 * recipe-detail refactor plan).
 *
 * The recipe import yaml is held only as a string (`RecipeEnvironment.import` /
 * the GitHub loader `content`). There is no yaml parser in the codebase, and we
 * deliberately avoid adding one: discovery is a line scan, injection is a
 * targeted per-line rewrite. This keeps `projectImport`'s payload unchanged and
 * leaves every untouched line byte-for-byte intact.
 *
 * Ported faithfully from the live-validated prototype (proven against real
 * loader output) — see plan §4 and the risk register (#7, injection escaping).
 */

/**
 * A fillable secret discovered in the import yaml. `lineIndex` + `indent` are the
 * surgical coordinates injection uses, so a key shared across two services stays
 * unambiguous (target by line, never by global key match).
 */
export interface RecipeConfigVar {
  name: string;
  scope: 'project' | 'service';
  serviceHostname?: string;
  required: boolean;
  lineIndex: number;
  indent: string;
  label?: string;
  help?: string;
  masked?: boolean;
}

// An UPPER_SNAKE key with a bare / `null` / `~` value (optionally trailing
// comment). The UPPER_SNAKE convention disambiguates env vars from lowercase
// structural keys (`services` / `hostname` / `type` / `envSecrets` / ...).
const KEY_NULL = /^(\s*)([A-Z][A-Z0-9_]*):\s*(null|~)?\s*(#.*)?$/;

// A mapping value that opens a block scalar body (`|`, `>`, with optional
// chomping / indentation indicators and an optional trailing comment), e.g.
// `text: |-` or `SCRIPT: | # explanation`.
const BLOCK_SCALAR_OPEN = /:\s*[|>][-+0-9]*\s*(#.*)?$/;

// A new service list item (`- ...`) inside the `services:` sequence. Entering a
// new item resets the tracked hostname so a hostname-less service can never
// inherit a stale name from the previous entry.
const SERVICE_ITEM = /^\s*-\s/;

// `- hostname: <value>` — the list-item form only. A nested `hostname:` (e.g. a
// db sub-config) is structural, not a service identity, so it is ignored.
const SERVICE_HOSTNAME = /^\s*-\s+hostname:\s*(\S+)/;

// An `envVariables:` / `envSecrets:` block header (empty value).
const BLOCK_HEADER = /^(\s*)(envVariables|envSecrets):\s*$/;

// An UPPER_SNAKE key whose name signals a sensitive value that must be masked in
// the fill-in UI (rendered as `type=password` instead of plaintext `type=text`).
const SECRET_KEY = /(?:PASSWORD|PASSWD|PWD|SECRET|TOKEN|API_?KEY|ACCESS_?KEY|PRIVATE|CREDENTIAL)/;

/**
 * Humanize an UPPER_SNAKE env key into a readable label
 * (`DATABASE_PASSWORD` -> `Database password`): split on `_`, lowercase every
 * word, then capitalize only the first character of the whole label.
 */
function humanizeKey(key: string): string {
  const words = key.split('_').filter(Boolean).map((w) => w.toLowerCase());
  const joined = words.join(' ');
  return joined ? joined.charAt(0).toUpperCase() + joined.slice(1) : key;
}

function leadingIndentLength(line: string): number {
  const match = line.match(/^(\s*)/);
  return match ? match[1].length : 0;
}

/**
 * Split a document into lines while recording each line's terminator so the
 * rewritten output can be re-emitted byte-for-byte (a CRLF-sourced yaml stays
 * CRLF; an LF-sourced yaml stays LF; a mixed document keeps each ending).
 */
function splitLinesPreservingEndings(text: string): { lines: string[]; endings: string[] } {
  const lines: string[] = [];
  const endings: string[] = [];
  const re = /([^\r\n]*)(\r\n|\n|\r|$)/g;

  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    lines.push(match[1]);
    endings.push(match[2]);
    // The trailing `$` alternative produces a final empty zero-length match; stop
    // so we do not loop forever on it.
    if (match[2] === '' && re.lastIndex >= text.length) {
      break;
    }
  }

  return { lines, endings };
}

/**
 * Re-join lines with their original terminators (see `splitLinesPreservingEndings`).
 */
function joinLinesPreservingEndings(lines: string[], endings: string[]): string {
  let out = '';
  for (let i = 0; i < lines.length; i++) {
    out += lines[i] + (endings[i] ?? '');
  }
  return out;
}

/**
 * Scan the import yaml for fillable null secrets.
 *
 * A fillable secret is a bare / `null` / `~` UPPER_SNAKE key sitting directly
 * inside an `envVariables:` (project scope) or `envSecrets:` (service scope)
 * block. Values are classified by their tail:
 *   - bare / `null` / `~`  -> ask  (returned)
 *   - `""` / `''`          -> intentionally empty (skipped)
 *   - `<@...>`             -> backend-generated (skipped)
 *   - `${ref}`             -> derivable reference (skipped)
 *   - any literal          -> already has a value (skipped)
 *
 * Comment lines and block-scalar (`|` / `>`) bodies are not scanned. A bare
 * UPPER_SNAKE key that is actually a mapping parent (a more-indented child block
 * follows) is also skipped — injecting it would orphan its children.
 *
 * Discovery failure is benign: over-asking surfaces one ignorable field,
 * under-asking is identical to today's behavior. Nothing is mutated.
 *
 * `required` defaults to `true` for every discovered null. `label` is a humanized
 * form of the key and `masked` is inferred from a secret-ish key pattern (so two
 * services declaring the same secret render as distinct password fields);
 * optionality and `help` are enriched later from Strapi metadata.
 */
export function scanNullSecrets(yaml: string): RecipeConfigVar[] {
  const { lines } = splitLinesPreservingEndings(yaml);
  const out: RecipeConfigVar[] = [];

  let block: 'envVariables' | 'envSecrets' | null = null;
  let blockIndent = -1;
  let hostname: string | undefined;
  let inBlockScalar = false;
  let scalarIndent = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const indent = leadingIndentLength(line);

    // Skip block-scalar bodies (`|` / `>`). A body line belongs to the scalar
    // while it is blank or more-indented than the key that opened it.
    if (inBlockScalar) {
      if (line.trim() && indent <= scalarIndent) {
        inBlockScalar = false;
      } else {
        continue;
      }
    }

    if (BLOCK_SCALAR_OPEN.test(line)) {
      inBlockScalar = true;
      scalarIndent = indent;
      continue;
    }

    if (!line.trim() || line.trim().startsWith('#')) {
      continue;
    }

    // A new service list item resets the tracked hostname so a hostname-less
    // service (or a nested db hostname) never inherits a stale name.
    if (SERVICE_ITEM.test(line)) {
      hostname = undefined;
    }

    const hostnameMatch = line.match(SERVICE_HOSTNAME);
    if (hostnameMatch) {
      hostname = hostnameMatch[1];
    }

    const blockHeaderMatch = line.match(BLOCK_HEADER);
    if (blockHeaderMatch) {
      block = blockHeaderMatch[2] as 'envVariables' | 'envSecrets';
      blockIndent = blockHeaderMatch[1].length;
      continue;
    }

    // Left the block once we de-indent back to (or past) its header level.
    if (block && indent <= blockIndent) {
      block = null;
      blockIndent = -1;
    }

    if (!block) {
      continue;
    }

    const keyMatch = line.match(KEY_NULL);
    if (keyMatch) {
      // Guard against a mapping-parent false positive: `NESTED_CONFIG:` followed
      // by a more-indented child block is a parent, not a fillable null scalar.
      // Injecting it would orphan the children under a quoted scalar -> invalid
      // yaml. Peek the next non-blank, non-comment line.
      if (nextBlockIsNested(lines, i, indent)) {
        continue;
      }

      const name = keyMatch[2];

      out.push({
        name,
        scope: block === 'envVariables' ? 'project' : 'service',
        serviceHostname: block === 'envSecrets' ? hostname : undefined,
        required: true,
        lineIndex: i,
        indent: keyMatch[1],
        label: humanizeKey(name),
        masked: SECRET_KEY.test(name),
      });
    }
  }

  return out;
}

/**
 * Peek past `i` to the next meaningful (non-blank, non-comment) line and report
 * whether it is more-indented than `keyIndent` — i.e. the key at `i` opens a
 * nested mapping / sequence body rather than holding a null scalar.
 */
function nextBlockIsNested(lines: string[], i: number, keyIndent: number): boolean {
  for (let j = i + 1; j < lines.length; j++) {
    const next = lines[j];
    const trimmed = next.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      continue;
    }
    return leadingIndentLength(next) > keyIndent;
  }
  return false;
}

/**
 * Always-double-quote-and-escape a scalar value for emission into yaml.
 *
 * This is the one safety-critical function: a naive bare/single-quote approach
 * reproduces the unescaped-quote bug at yaml-builder.service.ts:137,142. Always
 * double-quoting means any value (`:` `#` spaces quotes `\`) is safe.
 *
 * Order matters: backslash is escaped first so the escapes we add afterwards are
 * not themselves doubled.
 *
 * After the readable escapes, every remaining control character is hex-escaped so
 * the emitted scalar is a single physical line a strict YAML parser (Go yaml.v3
 * on the backend) accepts. This covers all C0 controls (0x00-0x1F) the named
 * escapes did not handle, 0x7F (DEL), and the Unicode line separators YAML treats
 * as breaks — NEL (U+0085), LS (U+2028), PS (U+2029). Each is emitted as `\xXX`
 * (code point <= 0xFF) or `\uXXXX` (otherwise). \n \r \t are handled above and so
 * never reach this final pass.
 */
export function toYamlDoubleQuoted(value: string): string {
  const escaped = String(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
    // Matching control chars here is the whole point of this pass.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F\u0085\u2028\u2029]/g, (ch) => {
      const code = ch.charCodeAt(0);
      return code <= 0xFF
        ? `\\x${code.toString(16).toUpperCase().padStart(2, '0')}`
        : `\\u${code.toString(16).toUpperCase().padStart(4, '0')}`;
    });

  return `"${escaped}"`;
}

/**
 * Inject filled values back into the import yaml by rewriting only the recorded
 * lines. Each rewritten line becomes `${indent}${name}: ${toYamlDoubleQuoted(value)}`;
 * every other line is left byte-identical, and each line keeps its original
 * terminator so a CRLF document round-trips byte-for-byte.
 *
 * Targeting by `lineIndex` (not a global key match) keeps a key shared across two
 * services unambiguous.
 *
 * Limitation (acceptable, see plan §4): single-line scalar values only — fine for
 * human secrets (API keys, passwords); multi-line / structured values are out of
 * scope.
 */
export function injectSecrets(
  yaml: string,
  values: Array<{ lineIndex: number; indent: string; name: string; value: string }>
): string {
  const { lines, endings } = splitLinesPreservingEndings(yaml);

  for (const { lineIndex, indent, name, value } of values) {
    lines[lineIndex] = `${indent}${name}: ${toYamlDoubleQuoted(value)}`;
  }

  return joinLinesPreservingEndings(lines, endings);
}
